import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Space,
  Tag,
  Typography,
  message
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import type { RouteViewModel } from '../../../types/routeViewModel';
import { useUpsertRouteStopsMutation } from '../../../store/api/routesApi';
import { buildMutationPolicyPreview, mapRejectMessage, policyFeedbackFromApi } from '../../mutationWorkspace/buildMutationPolicyPreview';
import { MutationImpactPreviewPanel } from '../../mutationWorkspace/MutationImpactPreviewPanel';
import { MutationReasonField } from '../../mutationWorkspace/MutationReasonField';
import { MutationStopEditorModal } from '../../mutationWorkspace/MutationStopEditorModal';
import {
  draftToSaveRow,
  mergeStopsForSave,
  stopLabel,
  viewStopToDraft
} from '../../mutationWorkspace/mutationStopHelpers';
import type { MutationStopDraft } from '../../mutationWorkspace/types';
import type { CorrectionContext } from '../hooks/useCorrectionContext';

const { Text } = Typography;

function splitStopsByBands(
  stops: MutationStopDraft[],
  ctx: CorrectionContext
): { completed: MutationStopDraft[]; current: MutationStopDraft[]; future: MutationStopDraft[] } {
  const completed: MutationStopDraft[] = [];
  const current: MutationStopDraft[] = [];
  const future: MutationStopDraft[] = [];

  for (const stop of [...stops].sort((a, b) => a.position - b.position)) {
    const stopId = stop.id;
    if (stopId == null) {
      future.push(stop);
      continue;
    }
    const band = ctx.getStopBand(stopId);
    if (band?.band === 'COMPLETED') completed.push(stop);
    else if (band?.band === 'CURRENT') current.push(stop);
    else if (band?.editable) future.push(stop);
    else future.push(stop);
  }

  return { completed, current, future };
}

type Props = {
  routeId: number;
  view: RouteViewModel;
  ctx: CorrectionContext;
  onSuccess: () => void;
  variant?: 'compact';
};

export function StopCorrectionPanel({ routeId, view, ctx, onSuccess, variant }: Props) {
  const [baseline, setBaseline] = useState<MutationStopDraft[]>([]);
  const [draftStops, setDraftStops] = useState<MutationStopDraft[]>([]);
  const [mutationReason, setMutationReason] = useState<string | null>(null);
  const [confirmationProvided, setConfirmationProvided] = useState(false);
  const [editorStop, setEditorStop] = useState<MutationStopDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MutationStopDraft | null>(null);
  const [lastPolicyMessage, setLastPolicyMessage] = useState<string | null>(null);

  const [upsertStops, { isLoading: saving }] = useUpsertRouteStopsMutation();

  const syncFromView = useCallback(() => {
    const stops = (view.structure.stops ?? []).map((s, i) => viewStopToDraft(s, i));
    setBaseline(stops);
    setDraftStops(stops);
    setMutationReason(null);
    setConfirmationProvided(false);
    setLastPolicyMessage(null);
  }, [view.structure.stops]);

  useEffect(() => {
    syncFromView();
  }, [syncFromView]);

  const { completed, current, future } = useMemo(
    () => splitStopsByBands(draftStops, ctx),
    [draftStops, ctx]
  );

  const isDirty = useMemo(
    () => JSON.stringify(baseline) !== JSON.stringify(draftStops),
    [baseline, draftStops]
  );

  const preview = useMemo(
    () =>
      buildMutationPolicyPreview({
        baseline,
        draft: mergeStopsForSave([...completed, ...current], future)
      }),
    [baseline, completed, current, future]
  );

  const moveFuture = (uid: string, direction: 'up' | 'down') => {
    const editableFuture = future.filter((s) => s.id != null && ctx.futureEditableStopIds.includes(Number(s.id)));
    const futureUids = editableFuture.map((s) => s.uid);
    const idx = futureUids.indexOf(uid);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= editableFuture.length) return;

    const reordered = [...editableFuture];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];

    const locked = [...completed, ...current, ...future.filter((s) => !editableFuture.some((e) => e.uid === s.uid))];
    const minFuturePos = locked.length;
    const withPositions = reordered.map((s, i) => ({ ...s, position: minFuturePos + i }));
    setDraftStops([...completed, ...current, ...withPositions]);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDraftStops((prev) => prev.filter((s) => s.uid !== deleteTarget.uid));
    setDeleteTarget(null);
  };

  const handleSave = async () => {
    if (!ctx.isOperationAllowed('STOP_FUTURE_EDIT')) {
      message.error('Редактирование точек недоступно');
      return;
    }
    if (!mutationReason) {
      message.error('Укажите причину изменения');
      return;
    }
    if (preview.status === 'forbidden') {
      message.error(preview.message ?? 'Изменение запрещено');
      return;
    }
    if (
      (preview.requiresConfirmation || preview.status === 'confirmation_required') &&
      !confirmationProvided
    ) {
      message.error('Подтвердите изменение');
      return;
    }
    if (!isDirty) {
      message.info('Нет изменений для сохранения');
      return;
    }

    const payload = mergeStopsForSave([...completed, ...current], future).map(draftToSaveRow);

    try {
      const result = await upsertStops({
        routeId,
        stops: payload,
        mutationReason,
        expectedUpdatedAt: ctx.routeUpdatedAt,
        confirmationProvided: confirmationProvided || undefined
      }).unwrap();

      const apiPreview = policyFeedbackFromApi(result.liveTopology, undefined);
      message.success('Изменения точек сохранены');
      setMutationReason(null);
      setConfirmationProvided(false);
      setLastPolicyMessage(apiPreview.message);
      onSuccess();
    } catch (e: unknown) {
      const err = e as { data?: unknown; status?: number };
      const msg =
        err.status === 409
          ? 'Маршрут был изменён другим пользователем. Обновите карточку и повторите.'
          : mapRejectMessage(err.data) ?? 'Не удалось сохранить изменения';
      setLastPolicyMessage(msg);
      message.error(msg);
    }
  };

  if (!ctx.isOperationAllowed('STOP_FUTURE_EDIT')) {
    return <Text type="secondary">Редактирование будущих точек недоступно.</Text>;
  }

  const editableFuture = future.filter(
    (s) => s.id != null && ctx.futureEditableStopIds.includes(Number(s.id))
  );

  return (
    <div className="mutation-workspace__section">
      {variant === 'compact' ? null : (
        <Alert
          type="info"
          showIcon
          message="Корректировка точек"
          description="Редактируются только будущие точки по данным сервера. Добавление новой погрузки с грузом — в разработке."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="mutation-workspace__section-title">Выполнено</div>
      {completed.length === 0 ? (
        <Text type="secondary">Нет выполненных точек</Text>
      ) : (
        <div className="mutation-workspace__executed-list">
          {completed.map((stop) => (
            <div key={stop.uid} className="mutation-workspace__stop-row mutation-workspace__stop-row--executed">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Tag>{stop.type === 'loading' ? 'Погрузка' : 'Выгрузка'}</Tag>
                <Text>{stopLabel(stop)}</Text>
              </Space>
            </div>
          ))}
        </div>
      )}

      {current.length > 0 && (
        <>
          <div className="mutation-workspace__section-title" style={{ marginTop: 16 }}>
            Текущая точка
          </div>
          {current.map((stop) => (
            <div key={stop.uid} className="mutation-workspace__stop-row">
              <Space>
                <Tag color="processing">{stop.type === 'loading' ? 'Погрузка' : 'Выгрузка'}</Tag>
                <Text>{stopLabel(stop)}</Text>
              </Space>
            </div>
          ))}
        </>
      )}

      <div className="mutation-workspace__section-title" style={{ marginTop: 16 }}>
        Будущие точки
      </div>
      {editableFuture.length === 0 ? (
        <Text type="secondary">Нет редактируемых будущих точек</Text>
      ) : (
        <div className="mutation-workspace__future-list">
          {editableFuture.map((stop, idx) => (
            <div key={stop.uid} className="mutation-workspace__stop-row mutation-workspace__stop-row--future">
              <Space direction="vertical" size={0}>
                <Space>
                  <Tag color="blue">{stop.type === 'loading' ? 'Погрузка' : 'Выгрузка'}</Tag>
                  <Text strong>{stopLabel(stop)}</Text>
                </Space>
              </Space>
              <div className="mutation-workspace__stop-actions">
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={idx === 0}
                  onClick={() => moveFuture(stop.uid, 'up')}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={idx === editableFuture.length - 1}
                  onClick={() => moveFuture(stop.uid, 'down')}
                />
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setEditorStop(stop)}
                />
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteTarget(stop)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <MutationReasonField value={mutationReason} onChange={setMutationReason} disabled={saving} />
      </div>

      {isDirty && (
        <MutationImpactPreviewPanel
          preview={preview}
          confirmationProvided={confirmationProvided}
          onConfirmationChange={setConfirmationProvided}
        />
      )}

      {lastPolicyMessage && (
        <Alert type="warning" message={lastPolicyMessage} showIcon closable style={{ marginTop: 12 }} />
      )}

      <Space style={{ marginTop: 16 }}>
        <Button onClick={syncFromView} disabled={!isDirty || saving}>
          Сбросить
        </Button>
        <Button type="primary" loading={saving} onClick={handleSave} disabled={!isDirty}>
          Сохранить точки
        </Button>
      </Space>

      <MutationStopEditorModal
        open={editorStop != null}
        stop={editorStop}
        title="Редактирование точки"
        onCancel={() => setEditorStop(null)}
        onSave={(stop) => {
          setDraftStops((prev) => prev.map((s) => (s.uid === stop.uid ? stop : s)));
          setEditorStop(null);
        }}
      />

      <Modal
        open={deleteTarget != null}
        title="Удаление точки"
        okText="Удалить"
        okButtonProps={{ danger: true }}
        onCancel={() => setDeleteTarget(null)}
        onOk={confirmDelete}
      >
        {deleteTarget && (
          <p>
            Будет удалена точка: <Text strong>{stopLabel(deleteTarget)}</Text>
          </p>
        )}
      </Modal>
    </div>
  );
}
